// Free-run practice pulse: a steady metronome beat for when the song is paused.
// Like CountIn, it owns no timer — the caller advances it by the RAF frame delta
// (dt × tempo, in music-seconds), so it never becomes a second clock. Run at the
// song's own beat period and meter it pulses in time with the song (the metronome
// hands off to the real playhead on play, so the click stays continuous). Pure.

export class PracticePulse {
  private acc = 0; // music-seconds since the last beat fired
  private beat = 0; // running beat index, for the per-bar downbeat accent

  constructor(
    readonly period: number,
    private readonly beatsPerBar: number,
  ) {}

  /** Restart the count at a downbeat (so a fresh free-run begins on an accent). */
  reset(): void {
    this.acc = 0;
    this.beat = 0;
  }

  /** Music-seconds until this pulse's next click — the click's phase, which the beat
   *  alignment holds the playhead against (see `beat-sync.ts`). */
  timeToNextClick(): number {
    return Math.max(0, this.period - this.acc);
  }

  /** Re-anchor the phase to the grid: `sinceLastBeat` is how far the playhead has swept
   *  past the grid beat it just crossed, so the next click lands one period later — i.e. on
   *  the next grid beat. Kept locked to the grid *while the pulse is silent* (the playhead
   *  is sounding the grid beats), so its fill-ins in a beatless tail and its clicks across a
   *  seek stay on the beat instead of drifting. This is not the forbidden mid-stream reset —
   *  it re-phases nothing audible, it holds the silent pulse on the grid it will resume. */
  anchor(sinceLastBeat: number): void {
    const p = this.period;
    this.acc = ((sinceLastBeat % p) + p) % p;
  }

  /** Advance by one frame's music time; returns one entry per beat that came due,
   *  `true` when that beat is a bar's downbeat (the accented click). */
  advance(musicDt: number): boolean[] {
    const accents: boolean[] = [];
    this.acc += musicDt;
    while (this.acc >= this.period) {
      this.acc -= this.period;
      accents.push(this.beat % this.beatsPerBar === 0);
      this.beat++;
    }
    return accents;
  }
}
